import { useEffect, useState } from 'react';
import { Button, Col, Divider, Drawer, Form, message, Row, Select, Space, Typography, Upload } from 'antd';
import { drawerWidth, formItemLayout, notifyMessages } from '../../utils/constants';
import { SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { useCurrentUserContext } from '../../context/currentUserContext';
import { useSelector } from 'react-redux';
import { isArchivio, isSuperAdmin } from '../../configs/auth/roleRights';
import AttachmentServices from '../../services/attachmentServices';
import { generateNewFileName, generateRandomCode } from '../../utils/functions';

const { Text } = Typography;
const { Dragger } = Upload;

export const ALLOWED_EXTENSIONS = ['.pdf'];
export const ALLOWED_TYPES = [
    'application/pdf'
];

const NewAttachment = ({ open, onClose, getAttachments }) => {

    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const { currentUser } = useCurrentUserContext();
    const suppliersList = useSelector(state => state.suppliers.suppliers);
    const [selectedSupplier, setSelectedSupplier] = useState(null);


    useEffect(() => {
        if (isArchivio(currentUser)) {
            const supplierData = {
                label: currentUser.name + " " + currentUser.surname,
                value: currentUser.supplierID
            }
            setSelectedSupplier(supplierData)
        }

    }, []);

    const normFile = (e) => {
        // Normalize file event to file list
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const saveData = async (values) => {

        const fileList = values.file;
        if (!fileList || fileList.length === 0) {
            message.error('Please upload a file.');
            return;
        }

        const file = fileList[0].originFileObj;
        const formData = new FormData();

        formData.append('File', file);
        formData.append('CategoryID', currentUser.lastSelectedCategory);
        formData.append('UserID', currentUser.id);

        if (!isArchivio(currentUser)) {
            formData.append('SupplierID', values.supplierID);
        }
        else {
            formData.append('SupplierID', currentUser.supplierID);
        }

        setLoadingButton(true);
        try {
            const respData = await AttachmentServices.addAttachments(formData);
            if (respData.success) {
                message.success(notifyMessages.operationSuccess);
                onClose();
                getAttachments();
            }
            else {
                message.error(notifyMessages.errorAccured);
            }
        } catch (error) {
            message.error(notifyMessages.errorAccured);
        }
        finally {
            setLoadingButton(false);
        }
    };

    const onSupplierSelect = (supplierId) => {
        const supplier = suppliersList.find(item => item.value === supplierId);
        setSelectedSupplier(supplier);
    }


    const beforeUpload = (file) => {
        const isExcelType = ALLOWED_TYPES.includes(file.type);
        const isExcelExt = ALLOWED_EXTENSIONS.some(ext =>
            file.name.toLowerCase().endsWith(ext)
        );

        if (!isExcelType && !isExcelExt) {
            message.error('Sono consentiti solo file PDF!');
            return Upload.LIST_IGNORE;
        }

        // Rename file
        // const newFileName = `${selectedSupplier.label}-${generateRandomCode()}-${file.name}}`;
        const newFileName = generateNewFileName(selectedSupplier.label, file.name);

        // Create a new File object
        const renamedFile = new File([file], newFileName, {
            type: file.type,
        });
        return Promise.resolve(renamedFile);
    };


    const props = {
        accept: '.pdf',
        maxCount: 1,
        beforeUpload: beforeUpload,
        showUploadList: true,
        customRequest: ({ file, onSuccess }) => {
            setTimeout(() => {
                onSuccess("ok");
            }, 500);
        },
    };

    return (
        <Drawer
            title="Aggiungi attachment"
            width={drawerWidth}
            onClose={onClose}
            destroyOnClose={true}
            open={open}
            extra={
                <Space>
                    <Button
                        form={"newAttachment"}
                        loading={loadingButton}
                        icon={<SaveOutlined />}
                        type="primary"
                        htmlType="submit"
                        key="submit"
                        disabled={!fieldsChanged}
                    >
                        Carica</Button>

                    <Button key="close" onClick={onClose}>Chiudi</Button>
                </Space>
            }
        >
            <Form
                id="newAttachment"
                layout="vertical"
                {...formItemLayout}
                onFinish={(values) => saveData(values)}
                onValuesChange={() => setFieldsChanged(true)}
            >
                <Row gutter={16}>

                    {!isArchivio(currentUser) && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label="Manutentore"
                                name="supplierID"
                                rules={[{ required: true, message: notifyMessages.requiredField }]}
                            >
                                <Select
                                    style={{ width: "100%" }}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Manutentore"
                                    options={suppliersList}
                                    onChange={(option) => onSupplierSelect(option)}
                                />
                            </Form.Item>
                        </Col>
                    )}

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Form.Item
                            label="File"
                            name="file"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[{ required: true, message: notifyMessages.requiredField }]}
                        >
                            <Upload
                                accept=".pdf"
                                disabled={!selectedSupplier}
                                maxCount={1}
                                beforeUpload={beforeUpload}
                                showUploadList={true}
                                customRequest={({ file, onSuccess }) => {
                                    setTimeout(() => {
                                        onSuccess("ok");
                                    }, 500);
                                }}
                            >
                                <Button disabled={!selectedSupplier} icon={<UploadOutlined />}>Seleziona file</Button>
                            </Upload>

                            {/* <Dragger {...props}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Fare clic o trascinare il file PDF in quest'area per caricarlo</p>
                                <p className="ant-upload-hint">Only one PDF file is allowed.</p>
                            </Dragger> */}

                        </Form.Item>
                    </Col>

                    <Divider />

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Space direction='vertical'>
                            <Text type="danger" italic>* Puoi caricare solo file PDF</Text>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
}
export default NewAttachment;